/**
 * Tiny DOM helpers. The only rule that matters here is the escaping one.
 *
 * Everything this app renders came from somewhere untrusted: a descriptor pasted from a chat
 * thread, an address decoded out of a PSBT a coordinator built, a label from a file. Building
 * markup with plain template strings and `innerHTML` would make an injected `<script>` in a
 * descriptor comment a live XSS on the surface where somebody authorises a Bitcoin spend.
 *
 * So the `html` tagged template escapes EVERY interpolation by default, and opting out requires
 * writing `raw(...)` explicitly. Safe is the thing you get for free; unsafe has to be typed.
 */

/**
 * The characters that break out of a text node or a QUOTED attribute, plus the ones that break
 * out of an UNQUOTED one.
 *
 * The second group is the non-obvious half. `<img src=${value}>` has no quotes, so the HTML
 * tokenizer ends the attribute value at the first whitespace and `x onerror=alert(1)` is a live
 * handler without using any of `&<>"'`. Escaping whitespace stops the break, and escaping `=`
 * and a backtick stops the two characters that could still start a value after one. Every call
 * site in views.ts uses quoted attributes today and a test pins that — this is so the first one
 * that forgets is harmless rather than an XSS on the screen where a spend is authorised.
 *
 * All of these are numeric character references, so they decode back to exactly the original
 * character: what the user sees, selects and copies is unchanged.
 */
const ENTITIES: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  ' ': '&#32;', '\t': '&#9;', '\n': '&#10;', '\f': '&#12;', '\r': '&#13;',
  '=': '&#61;', '`': '&#96;',
};

export const esc = (v: unknown): string =>
  String(v).replace(/[&<>"' \t\n\f\r=`]/g, (c) => ENTITIES[c]!);

/** Marks a string as already-safe markup. Deliberately verbose at the call site. */
export class Raw {
  constructor(readonly value: string) {}
  toString(): string { return this.value; }
}
export const raw = (s: string): Raw => new Raw(s);

function render(v: unknown): string {
  // `false` renders as empty so `${cond && html`…`}` omits the block — the common case. Booleans
  // that are meant to be VALUES (aria-pressed, aria-expanded) must therefore be stringified at
  // the call site with String(...); `aria-pressed="${x === y}"` would otherwise emit an empty
  // attribute, which assistive technology reads as "not a toggle" rather than "off".
  if (v === null || v === undefined || v === false) return '';
  if (v instanceof Raw) return v.value;
  if (Array.isArray(v)) return v.map(render).join('');
  return esc(v);
}

/** Escaping template literal. `html\`<p>${userInput}</p>\`` is safe; nesting `html` is too. */
export function html(strings: TemplateStringsArray, ...values: unknown[]): Raw {
  let out = strings[0]!;
  for (let i = 0; i < values.length; i++) out += render(values[i]) + strings[i + 1]!;
  return raw(out);
}

/* ── query + mount ────────────────────────────────────────────────────────── */

export const $ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document) =>
  root.querySelector<T>(sel);

export const $$ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document) =>
  Array.from(root.querySelectorAll<T>(sel));

export function mount(target: HTMLElement, content: Raw): void {
  target.innerHTML = content.value;
}

/** Delegated listener — survives re-renders, which plain addEventListener on a child does not. */
export function on<K extends keyof HTMLElementEventMap>(
  root: HTMLElement, type: K, selector: string,
  handler: (el: HTMLElement, ev: HTMLElementEventMap[K]) => void,
): void {
  root.addEventListener(type, (ev) => {
    const target = ev.target as HTMLElement | null;
    const el = target?.closest<HTMLElement>(selector);
    if (el && root.contains(el)) handler(el, ev as HTMLElementEventMap[K]);
  });
}

/* ── formatting ───────────────────────────────────────────────────────────── */

/**
 * Sats to BTC, always 8 decimal places, never rounded for display.
 *
 * Amounts are the thing a human checks against what they were told. Locale-aware grouping is
 * deliberately NOT used: a thin space or a comma in the middle of a Bitcoin amount is one more
 * thing that can be misread, and `2,50000000` vs `2.50000000` differ by a factor nobody wants
 * to discover afterwards.
 */
/**
 * Sats to BTC, by integer division.
 *
 * `sats / 1e8` is a double, and a PSBT amount is a uint64 — above 2^53 the number rendered here
 * would stop matching the number being spent, on the one screen where that has to be true.
 */
export const btc = (sats: bigint): string => {
  const neg = sats < 0n;
  const abs = neg ? -sats : sats;
  return `${neg ? '-' : ''}${abs / 100_000_000n}.${(abs % 100_000_000n).toString().padStart(8, '0')}`;
};

/** Middle-elides a long identifier, keeping enough of both ends to compare by eye. */
export const short = (s: string, head = 10, tail = 6): string =>
  s.length <= head + tail + 1 ? s : `${s.slice(0, head)}…${s.slice(-tail)}`;
